// Engine lab (dev page): plays a hard-coded loop through the AudioWorklet engine and shows the
// numbers that prove it runs (clock events, peak level, isolation). Used by the E1 e2e test.
import { useEffect, useState } from "react";
import { periodSpec } from "@eb/shared";
import { engineHost, type EngineHost } from "../audio/host";
import { DEMO_PERIOD, demoLoop } from "../dev/demoLoop";
import { useTitle } from "../lib/router";

declare global {
  interface Window {
    __eb?: { host?: EngineHost; peak: number; clocks: number; hits: number };
  }
}

export function Lab() {
  useTitle("Engine lab");
  const [state, setState] = useState<"idle" | "loading" | "playing" | "error">("idle");
  const [stats, setStats] = useState({ clocks: 0, peak: 0, voices: 0, isolated: false, rate: 0 });
  const [error, setError] = useState("");

  useEffect(() => {
    if (state !== "playing") return;
    const id = setInterval(() => {
      const d = window.__eb;
      if (!d?.host) return;
      setStats({
        clocks: d.clocks,
        peak: d.peak,
        voices: d.host.lastClock?.activeVoices ?? 0,
        isolated: d.host.isolated,
        rate: d.host.sampleRate,
      });
    }, 200);
    return () => clearInterval(id);
  }, [state]);

  async function start() {
    setState("loading");
    try {
      const host = await engineHost();
      window.__eb = { host, peak: 0, clocks: 0, hits: 0 };
      host.addListener({
        clock: (c) => {
          const d = window.__eb!;
          d.clocks++;
          d.peak = Math.max(d.peak, c.masterPeak);
        },
      });
      await host.resume();
      const manifest = await host.loadKit(DEMO_PERIOD.kit);
      host.setEditor(periodSpec(DEMO_PERIOD, manifest), demoLoop(), { type: "loop" });
      host.play();
      setState("playing");
    } catch (e) {
      setError(String(e));
      setState("error");
    }
  }

  async function stop() {
    (await engineHost()).stop();
    setState("idle");
  }

  return (
    <main className="page">
      <h1>Engine lab</h1>
      <p>Plays a hard-coded loop through the WASM engine in an AudioWorklet.</p>
      {state === "playing" ? (
        <button type="button" onClick={stop}>
          Stop
        </button>
      ) : (
        <button type="button" onClick={start} disabled={state === "loading"} data-testid="lab-start">
          {state === "loading" ? "Loading…" : "Play demo loop"}
        </button>
      )}
      {error && <p role="alert">{error}</p>}
      <dl data-testid="lab-stats" data-clocks={stats.clocks} data-peak={stats.peak.toFixed(3)}>
        <dt>State</dt>
        <dd data-testid="lab-state">{state}</dd>
        <dt>Clock events</dt>
        <dd>{stats.clocks}</dd>
        <dt>Peak</dt>
        <dd>{stats.peak.toFixed(3)}</dd>
        <dt>Voices</dt>
        <dd>{stats.voices}</dd>
        <dt>Sample rate</dt>
        <dd>{stats.rate}</dd>
        <dt>Cross-origin isolated (SAB rings)</dt>
        <dd data-testid="lab-isolated">{String(stats.isolated)}</dd>
      </dl>
    </main>
  );
}
